import { useMemo } from 'react';
import { Button } from '@heroui/react';

import type { LiveEvent } from '../hooks/useLiveEvents.ts';

interface LiveEventsPanelProps {
  events: LiveEvent[];
  onClose: () => void;
}

/**
 * Right-hand drawer that streams `SandboxLiveEvent` broadcasts (DD-CFS-18)
 * as they arrive. We show the most recent first so progress at the top
 * never gets buried under earlier output. Each event renders compactly:
 * type tag + relative time + a JSON detail block (collapsed by default).
 */
export function LiveEventsPanel({ events, onClose }: LiveEventsPanelProps) {
  const reversed = useMemo(() => [...events].reverse(), [events]);
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center gap-2 border-b border-white/8 px-3 py-2 text-xs">
        <span className="font-semibold uppercase tracking-wider opacity-70">
          Live events
        </span>
        <span className="opacity-50">{events.length}</span>
        <Button
          size="sm"
          variant="ghost"
          isIconOnly
          aria-label="Close live events"
          onPress={onClose}
          className="ms-auto"
        >
          <span aria-hidden="true">✕</span>
        </Button>
      </div>
      {events.length === 0 ? (
        <p className="px-3 py-4 text-xs opacity-60">
          No sandbox or watch activity yet. Tool runs that stream output
          (e.g. <code translate="no">sandbox_exec_stream</code>) will appear
          here in real time.
        </p>
      ) : (
        <ol className="scroll-pane min-h-0 flex-1 overflow-y-auto">
          {reversed.map((e) => (
            <li
              key={e.id}
              className="border-b border-white/5 px-3 py-2 text-[11px]"
            >
              <EventRow event={e} />
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function EventRow({ event }: { event: LiveEvent }) {
  const data = event.data as { type?: string; toolCallId?: string } | undefined;
  const type = data?.type ?? 'unknown';
  return (
    <details>
      <summary className="flex cursor-pointer items-center gap-2 select-none">
        <span className="rounded bg-white/8 px-1.5 py-0.5 font-mono text-[10px]" translate="no">
          {type}
        </span>
        <span className="opacity-50" translate="no">
          {data?.toolCallId ? `· ${data.toolCallId.slice(0, 8)}` : ''}
        </span>
        <time className="ms-auto opacity-50">
          {formatTime(event.receivedAt)}
        </time>
      </summary>
      <pre className="mt-1 max-h-60 overflow-auto rounded bg-black/40 p-2 text-[10px]">
        {safeStringify(event.data)}
      </pre>
    </details>
  );
}

function formatTime(t: number): string {
  // Use the user's locale; falls back to en-US if Intl is missing.
  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).format(t);
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}
